import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, imageUrl, readingTime } from "@/lib/api";
import { sectionName } from "@/lib/sections";
import { ArticleMeta, ContentNote } from "@/components/site/Meta";
import { Markdown } from "@/components/site/Markdown";
import { ArticleList } from "@/components/site/ArticleItem";
import NotFound from "@/pages/NotFound";
import { ShareButtons } from "@/components/site/ShareButtons";
import { NewsletterSignup } from "@/components/site/NewsletterSignup";
import { AudioPlayer } from "@/components/site/AudioPlayer";
import { SeriesBox, SeriesLabel } from "@/components/site/Series";
import { MostRead } from "@/components/site/MostRead";
import { Corrections } from "@/components/site/Corrections";

export const ArticleView = ({ a }) => (
  <article className="max-w-2xl mx-auto fade-up" data-testid="article-view">
    <ArticleMeta article={a} />
    <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.06] tracking-tight" data-testid="article-headline">
      {a.headline}
    </h1>
    {a.standfirst && (
      <p className="mt-6 font-display italic text-xl sm:text-2xl text-[var(--ink-2)] leading-snug border-l-2 border-[var(--rule)] pl-5" data-testid="article-standfirst">
        {a.standfirst}
      </p>
    )}
    <p className="mt-6 meta !normal-case !tracking-normal !font-normal" data-testid="article-reading-time">{readingTime(a.body)} min read</p>
    {a.series && <SeriesLabel a={a} />}
    {a.content_note && <ContentNote note={a.content_note} />}
    {a.image_path && (
      <figure className="my-10 -mx-5 sm:mx-0">
        <img src={imageUrl(a.image_path)} alt="" className="w-full h-auto" data-testid="article-lead-image" />
      </figure>
    )}
    {a.audio_url && <AudioPlayer url={a.audio_url} />}
    <div className="mt-10"><Markdown body={a.body} /></div>
    {a.corrections?.length > 0 && <Corrections items={a.corrections} />}
  </article>
);

export default function ArticlePage() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true });
  const [more, setMore] = useState([]);

  useEffect(() => {
    setState({ loading: true });
    api.get(`/articles/${slug}`)
      .then((r) => {
        setState({ article: r.data });
        document.title = `${r.data.headline} | ukdiscovered`;
        api.get("/articles", { params: { section: r.data.section, limit: 6 } })
          .then((m) => setMore(m.data.filter((x) => x.slug !== slug).slice(0, 4)));
      })
      .catch(() => setState({ notFound: true }));
  }, [slug]);

  if (state.loading) return <p className="py-24 meta">Loading</p>;
  if (state.notFound) return <NotFound />;
  const a = state.article;
  return (
    <div className="pt-14" data-testid="article-page">
      <ArticleView a={a} />
      {a.series && <SeriesBox a={a} />}
      <div className="max-w-2xl mx-auto mt-14 pt-6 border-t border-[var(--rule)]"><ShareButtons article={a} /></div>
      <div className="max-w-2xl mx-auto mt-10"><NewsletterSignup variant="boxed" id="article-newsletter" /></div>
      <MostRead excludeSlug={a.slug} className="max-w-2xl mx-auto mt-16" id="article-most-read" />
      <section className="max-w-2xl mx-auto mt-20 pt-6 border-t border-[var(--ink)]" data-testid="more-from-section">
        <div className="flex items-baseline justify-between">
          <h2 className="meta !text-[var(--ink)]">More from {sectionName(a.section)}</h2>
          <Link to={`/section/${a.section}`} className="meta link-quiet" data-testid="more-from-section-link">View all</Link>
        </div>
        <ArticleList articles={more} empty="Nothing else in this section yet." testId="more-articles" />
      </section>
    </div>
  );
}
